import type { PageModule } from "./page-module"

/** SPブレークポイント（この幅以下はロゴが position:absolute で流れる＝対象外） */
const SP_MAX = 1024

/**
 * 固定ロゴを、背後に来たセクションの明暗で白⇔黒に切り替える（トップ用）。
 *
 * 明るいセクション（`[data-logo-light]`）がロゴ中心の1pxトリガーラインに掛かっている間だけ
 * `.header__logo` に `is-onLight` を付与（→ CSSでロゴを黒に）。暗い背景では既定の白のまま。
 * 判定は header-nav.ts のフッター白化(`is-onDark`)と同じ「ロゴ中心Yのトリガーライン」方式。
 *
 * PC限定：SPはロゴが `position:absolute` でヒーロー後に画面外へ流れるため不要（resizeで随時判定）。
 * `is-onDark`(フッター用)とは別クラス＝1pxラインには同時に2セクション乗らず排他的にしか付かない＝衝突しない。
 * ロゴは <main> 外の永続要素なので document から引く（scope=main には無い）。
 *
 * @param {HTMLElement} scope - 現在ページの <main>
 * @returns {(() => void) | void} 後始末（対象セクションが無ければ何もしない）
 */
export const mountLogoContrast: PageModule = (scope) => {
  const sections = Array.from(scope.querySelectorAll<HTMLElement>("[data-logo-light]"))
  const logo = document.querySelector<HTMLElement>(".header__logo")
  if (!sections.length || !logo || !("IntersectionObserver" in window)) return

  /** 現在トリガーラインに掛かっている明るいセクション（取りこぼし防止に Set 保持） */
  const lit = new Set<Element>()
  let io: IntersectionObserver | null = null
  let resizeTimer: number | undefined

  /** ロゴ中心Yを1pxのトリガーラインにした IO を作り直す（初回・resize時。ロゴ位置=vw基準で変わるため） */
  const build = (): void => {
    io?.disconnect()
    lit.clear()
    // SP はロゴが固定でない（流れる）ので無効化して既定へ戻す
    if (window.innerWidth <= SP_MAX) {
      logo.classList.remove("is-onLight")
      return
    }
    const rect = logo.getBoundingClientRect()
    const lineY = rect.top + rect.height / 2 // 固定ロゴの中心Y＝判定ライン
    const vh = window.innerHeight
    io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => (e.isIntersecting ? lit.add(e.target) : lit.delete(e.target)))
        logo.classList.toggle("is-onLight", lit.size > 0)
      },
      { rootMargin: `-${lineY}px 0px -${vh - lineY - 1}px 0px` }
    )
    sections.forEach((s) => io?.observe(s))
  }

  build()

  /** resize：ロゴ位置(vw)とビューポート高が変わるので監視を作り直す（デバウンス200ms） */
  const onResize = (): void => {
    window.clearTimeout(resizeTimer)
    resizeTimer = window.setTimeout(build, 200)
  }
  window.addEventListener("resize", onResize)

  return () => {
    window.clearTimeout(resizeTimer)
    window.removeEventListener("resize", onResize)
    io?.disconnect()
    io = null
    logo.classList.remove("is-onLight")
  }
}
